/**
 * Manual highlighting for the Compare page.
 *
 * Select text inside a drug column → a "Highlight" button appears; click a
 * highlighted passage → "Remove highlight". A highlight marks every occurrence
 * of the selected text in that drug (case- and whitespace-insensitive, see
 * phraseMatch), on screen and in the exports.
 *
 * Painting uses the CSS Custom Highlight API: ranges are registered with the
 * browser instead of wrapping text in elements, so it works the same over every
 * viewer (SPL HTML, PDF text layers, JP sections, FDA letters) without touching
 * their DOM. Highlights live in page memory only — a refresh clears them.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { findPhraseSegments, normalizePhrase } from '../utils/phraseMatch';
import { USER_HIGHLIGHT_COLOR } from '../utils/highlightColors';
import { useTab } from '../shell/TabContext';

/** Put on a drug column's root element: its value is the drug id. */
export const USER_HIGHLIGHT_DRUG_ATTR = 'data-user-highlight-drug';
/** Put on parts of a column that must not be highlightable (e.g. its header). */
export const USER_HIGHLIGHT_IGNORE_ATTR = 'data-user-highlight-ignore';

const HIGHLIGHT_NAME = 'user-highlight';
const IGNORE_SELECTOR = `[${USER_HIGHLIGHT_IGNORE_ATTR}]`;
const TOOLBAR_ATTR = 'data-user-highlight-toolbar';
const MIN_CHARS = 2; // ignore accidental one-character selections
const REPAINT_DELAY_MS = 150;
// Translucent on screen so text drawn in a PDF page image stays readable
// underneath (same idea as the viewers' keyword marks).
const SCREEN_COLOR = `${USER_HIGHLIGHT_COLOR}99`;

/** drugId → the phrases the user highlighted in that drug. */
export type UserHighlights = Record<number, string[]>;

export function useUserHighlights() {
  const [highlights, setHighlights] = useState<UserHighlights>({});

  const add = useCallback((drugId: number, text: string) => {
    setHighlights((prev) => {
      const current = prev[drugId] ?? [];
      const key = normalizePhrase(text);
      if (current.some((p) => normalizePhrase(p) === key)) return prev;
      return { ...prev, [drugId]: [...current, text] };
    });
  }, []);

  const remove = useCallback((drugId: number, phrase: string) => {
    setHighlights((prev) => ({ ...prev, [drugId]: (prev[drugId] ?? []).filter((p) => p !== phrase) }));
  }, []);

  const clear = useCallback(() => setHighlights({}), []);
  const count = useMemo(() => Object.values(highlights).reduce((n, list) => n + list.length, 0), [highlights]);

  return { highlights, add, remove, clear, count };
}

type Painted = { drugId: number; phrase: string; ranges: Range[] };
type Toolbar =
  | { kind: 'add'; drugId: number; text: string; x: number; y: number }
  | { kind: 'remove'; drugId: number; phrase: string; x: number; y: number };

function supportsHighlights(): boolean {
  return typeof CSS !== 'undefined' && 'highlights' in CSS && typeof Highlight !== 'undefined';
}

/** Where every highlighted phrase currently is on screen, as DOM ranges. */
function locate(highlights: UserHighlights, scope: ParentNode): Painted[] {
  const painted: Painted[] = [];
  for (const [id, phrases] of Object.entries(highlights)) {
    const roots = Array.from(scope.querySelectorAll(`[${USER_HIGHLIGHT_DRUG_ATTR}="${id}"]`));
    for (const phrase of phrases) {
      const ranges = roots.flatMap((root) =>
        findPhraseSegments(root, phrase, IGNORE_SELECTOR).flat().map((seg) => {
          const range = document.createRange();
          range.setStart(seg.node, seg.start);
          range.setEnd(seg.node, seg.end);
          return range;
        }),
      );
      painted.push({ drugId: Number(id), phrase, ranges });
    }
  }
  return painted;
}

/** The drug column a node sits in, unless it's in an ignored part of it. */
function drugColumnOf(node: Node | null): Element | null {
  const el = node instanceof Element ? node : node?.parentElement ?? null;
  if (!el || el.closest(IGNORE_SELECTOR)) return null;
  return el.closest(`[${USER_HIGHLIGHT_DRUG_ATTR}]`);
}

/** "Highlight" toolbar for the current selection, if it lies within one drug column. */
function addToolbar(): Toolbar | null {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || !selection.rangeCount) return null;
  const text = selection.toString().trim();
  if (normalizePhrase(text).length < MIN_CHARS) return null;
  const column = drugColumnOf(selection.anchorNode);
  if (!column || column !== drugColumnOf(selection.focusNode)) return null;
  const rect = selection.getRangeAt(0).getBoundingClientRect();
  const drugId = Number(column.getAttribute(USER_HIGHLIGHT_DRUG_ATTR));
  return { kind: 'add', drugId, text, x: rect.left + rect.width / 2, y: rect.top };
}

function caretAt(x: number, y: number): { node: Node; offset: number } | null {
  if (document.caretPositionFromPoint) {
    const pos = document.caretPositionFromPoint(x, y);
    return pos ? { node: pos.offsetNode, offset: pos.offset } : null;
  }
  const range = document.caretRangeFromPoint?.(x, y);
  return range ? { node: range.startContainer, offset: range.startOffset } : null;
}

/** "Remove highlight" toolbar when the click landed on a highlighted passage. */
function removeToolbar(event: MouseEvent, painted: Painted[]): Toolbar | null {
  const caret = caretAt(event.clientX, event.clientY);
  if (!caret) return null;
  const hit = painted.find((p) => p.ranges.some((r) => r.isPointInRange(caret.node, caret.offset)));
  return hit ? { kind: 'remove', drugId: hit.drugId, phrase: hit.phrase, x: event.clientX, y: event.clientY } : null;
}

export default function UserHighlighter({
  highlights,
  onAdd,
  onRemove,
}: {
  highlights: UserHighlights;
  onAdd: (drugId: number, text: string) => void;
  onRemove: (drugId: number, phrase: string) => void;
}) {
  const painted = useRef<Painted[]>([]);
  // CSS.highlights is one registry for the whole document, and every shell tab
  // stays mounted: a hidden tab must neither paint into it nor react to the
  // active tab's mouse events.
  const { isActive, rootRef } = useTab();
  const [toolbar, setToolbar] = useState<Toolbar | null>(null);

  // Paint, and repaint whenever a viewer re-renders its text (PDF pages mount
  // as they scroll into view, sections expand, …).
  useEffect(() => {
    if (!isActive || !supportsHighlights()) return;
    const paint = () => {
      painted.current = locate(highlights, rootRef.current ?? document);
      CSS.highlights.set(HIGHLIGHT_NAME, new Highlight(...painted.current.flatMap((p) => p.ranges)));
    };
    paint();
    let timer: number | undefined;
    const observer = new MutationObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(paint, REPAINT_DELAY_MS);
    });
    if (Object.values(highlights).some((list) => list.length)) {
      observer.observe(rootRef.current ?? document.body, { childList: true, subtree: true, characterData: true });
    }
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
      CSS.highlights.delete(HIGHLIGHT_NAME);
      painted.current = [];
    };
  }, [highlights, isActive, rootRef]);

  // Selection → "Highlight"; click on a highlight → "Remove highlight".
  useEffect(() => {
    if (!isActive) return;
    const onMouseUp = (event: MouseEvent) => {
      if ((event.target as Element | null)?.closest?.(`[${TOOLBAR_ATTR}]`)) return;
      // Let the browser finish updating the selection for this mouseup first.
      window.setTimeout(() => setToolbar(addToolbar() ?? removeToolbar(event, painted.current)), 0);
    };
    const onMouseDown = (event: MouseEvent) => {
      if (!(event.target as Element | null)?.closest?.(`[${TOOLBAR_ATTR}]`)) setToolbar(null);
    };
    const hide = () => setToolbar(null);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', hide, true); // positions go stale on any scroll
    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', hide, true);
    };
  }, [isActive]);

  const apply = () => {
    if (!toolbar) return;
    if (toolbar.kind === 'add') {
      onAdd(toolbar.drugId, toolbar.text);
      window.getSelection()?.removeAllRanges();
    } else {
      onRemove(toolbar.drugId, toolbar.phrase);
    }
    setToolbar(null);
  };

  return (
    <>
      <style>{`::highlight(${HIGHLIGHT_NAME}) { background-color: ${SCREEN_COLOR}; }`}</style>
      {toolbar && (
        <div
          {...{ [TOOLBAR_ATTR]: '' }}
          className="fixed z-[60] -translate-x-1/2 -translate-y-full pb-1.5"
          style={{ left: toolbar.x, top: toolbar.y }}
        >
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()} // keep the text selection
            onClick={apply}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white shadow-lg hover:bg-slate-700"
          >
            <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: USER_HIGHLIGHT_COLOR }} aria-hidden />
            {toolbar.kind === 'add' ? 'Highlight' : 'Remove highlight'}
          </button>
        </div>
      )}
    </>
  );
}

/** Legend chip for the page's highlight row: count + clear-all. */
export function UserHighlightsChip({ count, onClear }: { count: number; onClear: () => void }) {
  if (!count) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
      <span className="inline-block h-3 w-3 shrink-0 rounded-sm ring-1 ring-slate-300" style={{ backgroundColor: USER_HIGHLIGHT_COLOR }} aria-hidden />
      Your highlights ({count})
      <button type="button" onClick={onClear} className="rounded px-1 text-indigo-600 hover:bg-indigo-50 hover:underline">
        Clear
      </button>
    </span>
  );
}
